import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSellerOrders, confirmOrder } from '@/api';
import type { SellerOrder } from '@/api';

const sellerTabs = ['全部', '挂卖中', '待买方签名', '待确认', '已完成', '已取消'];

export default function SellerWarehouse() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryTab = searchParams.get('tab') || '全部';
  const [activeTab, setActiveTab] = useState(sellerTabs.includes(queryTab) ? queryTab : '全部');
  const [orders, setOrders] = useState<SellerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const loadOrders = useCallback(async () => {
    try {
      const res = await getSellerOrders(0, 1, 50);
      setOrders(res.list || []);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered =
    activeTab === '全部'
      ? orders
      : orders.filter((o) => o.status === activeTab);

  const scrollToTab = (label: string) => {
    setActiveTab(label);
    const idx = sellerTabs.findIndex((t) => t === label);
    if (tabContainerRef.current && idx >= 0) {
      const tabEl = tabContainerRef.current.children[idx] as HTMLElement;
      if (tabEl) {
        const containerWidth = tabContainerRef.current.offsetWidth;
        const tabLeft = tabEl.offsetLeft;
        const tabWidth = tabEl.offsetWidth;
        tabContainerRef.current.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: 'smooth',
        });
      }
    }
  };

  /** 确认收款 */
  const handleConfirm = async (id: string) => {
    if (confirmingId) return;
    setConfirmingId(id);
    try {
      await confirmOrder(Number(id));
      showToast('确认收款成功');
      await loadOrders();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '操作失败');
    } finally {
      setConfirmingId(null);
    }
  };

  const getTabCount = (tab: string) => {
    if (tab !== '全部') {
      return orders.filter((o) => o.status === tab).length;
    }
    return undefined;
  };

  return (
    <div className="mobile-container bg-background-50 min-h-screen pb-28">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-background-200/50">
        <div className="flex items-center px-4 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground-700 cursor-pointer hover:bg-background-100 active:scale-95 transition-all"
            aria-label="返回"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <h1 className="flex-1 text-center text-foreground-950 text-base font-bold tracking-wide">
            卖单仓库
          </h1>
          <div className="w-9" />
        </div>
      </header>

      {/* Operation flow card */}
      <div className="mx-4 mt-4 p-4 rounded-2xl bg-secondary-50/60 border border-secondary-200/30">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-secondary-100 flex items-center justify-center">
            <i className="ri-book-open-line text-secondary-600 text-xs" />
          </div>
          <p className="text-foreground-900 text-sm font-bold">卖方操作流程</p>
        </div>
        <p className="text-foreground-500 text-xs leading-relaxed">
          等待买家签名确认付款 → 核实到账后点击"确认收款" → 买家将于14:30进行转拍操作
        </p>
      </div>

      {/* Tabs - pill style */}
      <div className="sticky top-12 z-30 bg-background-50/95 backdrop-blur-sm px-4 pt-3 pb-2">
        <div
          ref={tabContainerRef}
          className="flex overflow-x-auto gap-2 scrollbar-hide"
        >
          {sellerTabs.map((tab) => {
            const isActive = activeTab === tab;
            const count = getTabCount(tab);
            return (
              <button
                key={tab}
                onClick={() => scrollToTab(tab)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold cursor-pointer transition-all duration-300 whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500 text-white shadow-md shadow-primary-500/15'
                    : 'bg-white text-foreground-500 border border-background-200/60 hover:bg-background-100 hover:text-foreground-700'
                }`}
              >
                {tab}
                {count !== undefined && count > 0 && (
                  <span className={`ml-1 ${isActive ? 'text-white/80' : 'text-primary-500'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Order list */}
      <main className="px-4 pt-3 space-y-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
            <p className="text-foreground-400 text-sm">加载中...</p>
          </div>
        )}
        {error && !loading && (
          <div className="text-center py-20">
            <i className="ri-wifi-off-line text-foreground-300 text-3xl mb-3 block" />
            <p className="text-foreground-400 text-sm">{error}</p>
          </div>
        )}
        <>
            {!loading && !error && filtered.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-file-list-3-line text-foreground-300 text-3xl mb-3 block" />
                <p className="text-foreground-400 text-sm">暂无相关订单</p>
              </div>
            )}
            {!loading && !error && filtered.map((order) => (
          <div
            key={order.id}
            className="bg-white rounded-2xl overflow-hidden shadow-sm border border-background-200/40"
          >
            {/* Product info row */}
            <div className="px-4 py-4 flex gap-3">
              <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-background-100 ring-1 ring-background-200/50">
                {order.productImage ? (
                  <img
                    src={order.productImage}
                    alt={order.productName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-foreground-300 text-xl" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-foreground-900 text-sm font-bold">
                      {order.productName}
                    </p>
                    <p className="text-foreground-400 text-xs mt-0.5">
                      买家：{order.buyerName} {order.buyerPhone}
                    </p>
                  </div>
                  {order.canConfirm && !order.isConfirmed && (
                    <button
                      onClick={() => handleConfirm(order.id)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                        confirmingId === order.id
                          ? 'bg-accent-500 text-white'
                          : 'bg-primary-500 text-white hover:bg-primary-600 active:scale-95'
                      }`}
                    >
                      {confirmingId === order.id ? '确认中...' : '确认收款'}
                    </button>
                  )}
                </div>
                <p className="text-primary-500 text-base font-bold mt-1">
                  ¥{Number(order.price).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-4 h-px bg-background-200/60" />

            {/* Payment section：挂卖中尚无买家，不显示付款凭证区 */}
            {order.status !== '挂卖中' && (
            <div className="px-4 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <i className="ri-image-line text-foreground-400 text-xs" />
                  <span className="text-foreground-500 text-xs font-medium">买家签名图片</span>
                </div>
                {order.canConfirm && !order.isConfirmed && (
                  <span className="text-primary-500 text-xs font-bold">
                    {order.paymentStatus}
                  </span>
                )}
                {order.isConfirmed && (
                  <span className="text-accent-500 text-xs font-bold flex items-center gap-1">
                    <i className="ri-checkbox-circle-line text-sm" />
                    {order.paymentStatus}
                  </span>
                )}
              </div>
              {order.paymentImage ? (
                <div
                  className="relative rounded-xl overflow-hidden bg-background-50 ring-1 ring-background-200/50 cursor-pointer group"
                  onClick={() => setPreviewImage(order.paymentImage)}
                >
                  <img
                    src={order.paymentImage}
                    alt="买家签名图片"
                    className="w-full h-auto max-h-56 object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                  <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-zoom-in-line text-sm" />
                  </div>
                </div>
              ) : (
                <div className="py-6 rounded-xl bg-background-50 flex flex-col items-center justify-center gap-1">
                  <i className="ri-image-off-line text-foreground-300 text-xl" />
                  <span className="text-foreground-300 text-xs">买家未签名</span>
                </div>
              )}
              {order.paymentImage && (
                <p className="text-center text-foreground-300 text-xs mt-2.5">
                  点击图片可放大查看
                </p>
              )}
            </div>
            )}

            {/* Bottom action row */}
            <div className="px-4 py-3 bg-background-50/50 border-t border-background-200/30 flex items-center justify-between">
              <span className="text-foreground-400 text-xs">{order.purchaseDate}</span>
              {order.canConfirm && !order.isConfirmed && (
                <button
                  onClick={() => handleConfirm(order.id)}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 ${
                    confirmingId === order.id
                      ? 'bg-accent-500 text-white'
                      : 'bg-primary-500 text-white hover:bg-primary-600 active:scale-95 shadow-sm shadow-primary-500/15'
                  }`}
                >
                  <i className="ri-check-double-line" />
                  {confirmingId === order.id ? '确认中...' : '确认收款'}
                </button>
              )}
              {order.isConfirmed && (
                <span className="text-accent-500 text-xs font-bold flex items-center gap-1">
                  <i className="ri-checkbox-circle-line text-sm" />
                  已确认收款
                </span>
              )}
            </div>
          </div>
        ))}
        </>
      </main>

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-2xl text-white text-sm z-50 shadow-xl">
          {toast}
        </div>
      )}

      {/* Image preview modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={previewImage}
              alt="买家签名图片放大"
              className="max-w-full max-h-[80vh] rounded-xl object-contain"
            />
            <button
              className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white cursor-pointer hover:bg-white/30 transition-colors"
              onClick={() => setPreviewImage(null)}
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}